import React, { useEffect } from 'react';
import { Avatar } from '@hyperflake/react-ui-library';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import { authActions } from '@store/auth';
import store, { TRootState } from '@store/index';
import { profileAction } from '@store/profile';
import { SearchForAnythingBar } from 'modules/shared/features/searchForAnything';
import useAuthSelector from 'modules/shared/hooks/use-auth-selector';
import { CrossIcon, GridIcon, HamburgerMenuIcon, QuantamIcon, SearchIcon } from 'modules/shared/icons';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface AppNavbarMobileProps {
    onMenuClick: () => void;
    isMenuOpen: boolean;
    setOpen: (b: boolean) => void;
}

function AppNavbarMobile({ onMenuClick, isMenuOpen, setOpen }: AppNavbarMobileProps) {
    const navigate = useNavigate();
    const { profile } = useSelector((state: TRootState) => state);
    const { token } = useAuthSelector();
    const quantamAppUrl = `${import.meta.env.VITE_APP_RESELLER_APP_URL}/authorize?token=${token}`;

    const [searchOpen, setSearchOpen] = React.useState(false);

    // prevent body scroll when search sheet is open
    React.useEffect(() => {
        if (!searchOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [searchOpen]);
    const location = useLocation();
    useEffect(() => {
        if (isMenuOpen) setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (searchOpen) {
            setSearchOpen(false);
            // restore body scroll in case it was locked
            document.body.style.overflow = '';
        }
    }, [location.key]);

    return (
        <header className="lg:hidden fixed top-0 left-0 right-0 z-[80] h-[56px] border-b border-separator-color-light bg-card-color">
            <div className="flex h-full items-center gap-2 px-3">
                {/* Hamburger / Close toggle */}
                <button
                    type="button"
                    onClick={onMenuClick}
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-controls="mobile-sidebar"
                    aria-expanded={isMenuOpen}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border hover:bg-gray-50 active:scale-95 transition-all"
                >
                    {isMenuOpen ? <CrossIcon /> : <HamburgerMenuIcon />}
                </button>

                {/* Logo */}
                <Link to="/" className="inline-flex items-center">
                    <img className="h-5" src="/logos/logo.svg" alt="Progress Center Logo" />
                </Link>

                {/* Search icon */}
                <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    aria-label="Search"
                    className="ml-2 inline-flex h-10 w-10 items-center justify-center rounded-xl border hover:bg-gray-50 active:scale-95"
                >
                    <SearchIcon size={18} />
                </button>

                {/* Right section */}
                <div className="ml-auto flex items-center gap-3">
                    {/* App launcher */}
                    <Dropdown>
                        <DropdownTrigger className="flex items-center">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border hover:bg-gray-50">
                                <GridIcon />
                            </span>
                        </DropdownTrigger>
                        <DropdownList>
                            <DropdownItem onClick={() => window.open(quantamAppUrl, '_blank')}>
                                <div className="flex gap-2 items-center">
                                    <QuantamIcon />
                                    <h6>Quantam</h6>
                                </div>
                            </DropdownItem>
                            <DropdownItem>
                                <div className="flex gap-2 items-center">
                                    <img className="h-4" src="/logos/logo-icon.svg" />
                                    <h6>Pulse</h6>
                                </div>
                            </DropdownItem>
                        </DropdownList>
                    </Dropdown>

                    {/* Profile */}
                    <Dropdown>
                        <DropdownTrigger className="flex items-center">
                            <span className="inline-flex">
                                <Avatar src={profile?.info?.dp} alt={profile?.info?.name} />
                            </span>
                        </DropdownTrigger>
                        <DropdownList>
                            <DropdownItem onClick={() => navigate('/profile')}>Account</DropdownItem>
                            <DropdownItem
                                onClick={() => {
                                    store.dispatch(profileAction.removeProfile());
                                    store.dispatch(authActions.logout());
                                }}
                            >
                                <span className="text-danger">Log out</span>
                            </DropdownItem>
                        </DropdownList>
                    </Dropdown>
                </div>
            </div>

            {/* Full-width search sheet */}
            <div
                className={`fixed inset-0 z-[90] lg:hidden ${searchOpen ? '' : 'pointer-events-none'}`}
                aria-hidden={!searchOpen}
            >
                {/* Backdrop */}
                <div
                    className={`absolute inset-0 bg-black/40 transition-opacity ${
                        searchOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                    onClick={() => setSearchOpen(false)}
                />
                {/* Panel */}
                <div
                    className={`absolute left-0 right-0 top-0 rounded-b-2xl bg-card-color shadow-lg transition-transform ${
                        searchOpen ? 'translate-y-0' : '-translate-y-full'
                    }`}
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                        <div className="text-sm font-semibold">Search</div>
                        <button
                            type="button"
                            onClick={() => setSearchOpen(false)}
                            aria-label="Close search"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                        >
                            <CrossIcon size={18} />
                        </button>
                    </div>
                    <div className="p-3">
                        <SearchForAnythingBar key={location.key} />
                    </div>
                </div>
            </div>
        </header>
    );
}

export default AppNavbarMobile;
